import colors from '@/enum/colors'
import { getActiveLayoutColors } from '@/composables/globalData/useLayouts'
import { ColumnLabel } from '@/types/dashboard'
import type { TableAlignmentType } from '@/types/alignment'
const { inCall } = getActiveLayoutColors()

export interface MaxCallerCount {
    queueId: number | string,
    value: number,
    queueName: string,
    thresholdStatus: string
}

export interface ExitCount {
    billing_cdr_queue_type: number;
    ExitTypeCount: number;
}

export interface QueueData {
    queue_id: number;
    QueueMembers: QueueMember[];
    ExitCounts: ExitCount[];
    [key: string]: string | number | ExitCount[] | QueueMember[];
}

export interface QueueMember {
    ExtenID?: number
    LoginRequired: boolean
    UserID: number

}
export interface MultiQueueColumn {
    prop: string
    align: TableAlignmentType
    fixed: boolean
    label: string
    minWidth: number
}

export interface QueueFromEntities {
    AccountID: number
    q_name: string
    queue_id: number
}

export type QueuesFromEntities = QueueFromEntities[]

export interface FilteredMultiQueueColumn extends Omit<MultiQueueColumn, 'label'> {
    label: ColumnLabel
    textColor: string;
}

export interface MaxWaitTime extends MaxCallerCount {
    timerState?: number
}

export type MaxWaitTimes =  Record<number, Omit<MaxCallerCount, 'queueId'>>

export type MaxCallersCount = Record<number, Omit<MaxCallerCount, 'queueId'>>

export interface DefaultRowData {
    [key: string]: string | number;
}

export interface MappedBaseTableData {
    [key: number] : string | number
}

export const multiQueueColumnsPropNames = {
    QUEUE_NAME: 'queue_name',
    CALLERS: 'Callers',
    CURRENT_WAIT_TIME: 'CurrentWaitTime',
    ANSWER: 'Answer',
    ABANDONED: 'Abandoned',
    IVR_EXIT: 'IVRExit',
    PICK_UP: 'PickUp',
    TIMEOUT_EXIT: 'TimeOutExit',
    JOIN_EMPTY: 'JoinEmpty',
    LEAVE_EMPTY: 'LeaveEmpty',
    JOIN_UNAVAIL: 'JoinUnavail',
    LEAVE_UNAVAIL: 'LeaveUnavail',
    FULL: 'Full',
    NEXT_DESTINATION: 'NextDestination',
    CALL_COUNT: 'CallCount',
    MAX_RING_TIME: 'MaxRingTime',
    NOT_IN_SLA_COUNT: 'NotInSLACount',
    IN_SLA_COUNT: 'InSLACount',
    AVG_RING_TIME: 'AvgRingTime',
    ACTIVE_QUEUE_CALLS: 'ActiveQueueCalls',
    AGENTS_ASSIGNED_AND_AVAILABLE: 'AgentsAssignedAndAvailable',
    AGENTS_ASSIGNED_AND_UNAVAILABLE: 'AgentsAssignedAndUnavailable',
    RECURRING_COUNT: 'RecurringCount',
    AVG_ANSWER_HOURLY: 'AvgAnswerHourly',
    AVG_CALLS_HOURLY: 'AvgCallsHourly',
    TOTAL_ANSWERED_CALLBACKS: 'TotalAnsweredCallbacks',
    ANSWERED_WITH_CB_PERCENT: 'AnsweredWithCBPercent'
}

export const billingCdrQueueTypes:Record<number, string> = {
    1: multiQueueColumnsPropNames.ANSWER,
    2: multiQueueColumnsPropNames.ABANDONED,
    3: multiQueueColumnsPropNames.IVR_EXIT,
    4: multiQueueColumnsPropNames.PICK_UP,
    5: multiQueueColumnsPropNames.TIMEOUT_EXIT,
    6: multiQueueColumnsPropNames.JOIN_EMPTY,
    7: multiQueueColumnsPropNames.LEAVE_EMPTY,
    8: multiQueueColumnsPropNames.JOIN_UNAVAIL,
    9: multiQueueColumnsPropNames.LEAVE_UNAVAIL,
    10: multiQueueColumnsPropNames.FULL,
    11: multiQueueColumnsPropNames.NEXT_DESTINATION,
}

export const billingCdrQueueTypesReversed = Object.fromEntries(
    Object.entries(billingCdrQueueTypes).map(([ num, key ]) => [ key, Number(num) ])
) as Record<string, number>

export const queueDashboardDefaultColumnStyles = {
    [multiQueueColumnsPropNames.ANSWER]: {
        color: inCall,
    },
    [multiQueueColumnsPropNames.ABANDONED]: {
        color: colors.PRIVATE_COLOR,
    },
    [multiQueueColumnsPropNames.IVR_EXIT]: {
        color: colors.PRIVATE_COLOR,
    },
    [multiQueueColumnsPropNames.PICK_UP]: {
        color: colors.LOGIN_COLOR,
    },
    [multiQueueColumnsPropNames.TIMEOUT_EXIT]: {
        color: colors.ADMINISTRATIVE_COLOR,
    },
    [multiQueueColumnsPropNames.JOIN_EMPTY]: {
        color: colors.LUNCH_COLOR,
    },
    [multiQueueColumnsPropNames.LEAVE_EMPTY]: {
        color: colors.PRIVATE_COLOR,
    },
    [multiQueueColumnsPropNames.JOIN_UNAVAIL]: {
        color: colors.LOGOUT_COLOR,
    },
    [multiQueueColumnsPropNames.LEAVE_UNAVAIL]: {
        color: colors.PRIVATE_COLOR,
    },
    [multiQueueColumnsPropNames.FULL]: {
        color: colors.OTHER_COLOR,
    },
    [multiQueueColumnsPropNames.NEXT_DESTINATION]: {
        color: colors.LOGOUT_COLOR,
    },
}

export const defaultVisibleColumns = {
    [multiQueueColumnsPropNames.QUEUE_NAME]: true,
    [multiQueueColumnsPropNames.CALLERS]: true,
    [multiQueueColumnsPropNames.CURRENT_WAIT_TIME]: true,
    [multiQueueColumnsPropNames.ANSWER]: true,
    [multiQueueColumnsPropNames.ABANDONED]: true,
    [multiQueueColumnsPropNames.CALL_COUNT]: true,
    [multiQueueColumnsPropNames.MAX_RING_TIME]: true,
    [multiQueueColumnsPropNames.NOT_IN_SLA_COUNT]: true,
    [multiQueueColumnsPropNames.IN_SLA_COUNT]: true,
    [multiQueueColumnsPropNames.AVG_RING_TIME]: true,
    [multiQueueColumnsPropNames.AVG_ANSWER_HOURLY]: true,
}

export const defaultRowData: DefaultRowData = {
    [multiQueueColumnsPropNames.QUEUE_NAME]: '',
    [multiQueueColumnsPropNames.CALLERS]: 0,
    [multiQueueColumnsPropNames.CURRENT_WAIT_TIME]: '',
    [multiQueueColumnsPropNames.ANSWER]: 0,
    [multiQueueColumnsPropNames.ABANDONED]: 0,
    [multiQueueColumnsPropNames.IVR_EXIT]: 0,
    [multiQueueColumnsPropNames.PICK_UP]: 0,
    [multiQueueColumnsPropNames.TIMEOUT_EXIT]: 0,
    [multiQueueColumnsPropNames.JOIN_EMPTY]: 0,
    [multiQueueColumnsPropNames.LEAVE_EMPTY]: 0,
    [multiQueueColumnsPropNames.JOIN_UNAVAIL]: 0,
    [multiQueueColumnsPropNames.LEAVE_UNAVAIL]: 0,
    [multiQueueColumnsPropNames.FULL]: 0,
    [multiQueueColumnsPropNames.NEXT_DESTINATION]: 0,
    [multiQueueColumnsPropNames.CALL_COUNT]: 0,
    [multiQueueColumnsPropNames.MAX_RING_TIME]: 0,
    [multiQueueColumnsPropNames.NOT_IN_SLA_COUNT]: 0,
    [multiQueueColumnsPropNames.IN_SLA_COUNT]: 0,
    [multiQueueColumnsPropNames.AVG_RING_TIME]: 0,
    [multiQueueColumnsPropNames.ACTIVE_QUEUE_CALLS]: 0,
    [multiQueueColumnsPropNames.AGENTS_ASSIGNED_AND_AVAILABLE]: 0,
    [multiQueueColumnsPropNames.AGENTS_ASSIGNED_AND_UNAVAILABLE]: 0,
    [multiQueueColumnsPropNames.RECURRING_COUNT]: 0,
    [multiQueueColumnsPropNames.AVG_ANSWER_HOURLY]: 0,
    [multiQueueColumnsPropNames.TOTAL_ANSWERED_CALLBACKS]: 0,
    [multiQueueColumnsPropNames.ANSWERED_WITH_CB_PERCENT]: 0
}
export const defaultAvailableColumns: MultiQueueColumn[] = [
    {
        prop: multiQueueColumnsPropNames.QUEUE_NAME,
        align: 'center',
        fixed: false,
        label: 'Queue Name',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.CALLERS,
        align: 'center',
        fixed: false,
        label: 'Callers',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.CURRENT_WAIT_TIME,
        align: 'center',
        fixed: false,
        label: 'Current wait time',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.ANSWER,
        align: 'center',
        fixed: false,
        label: 'Answer',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.ABANDONED,
        align: 'center',
        fixed: false,
        label: 'Abandoned',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.IVR_EXIT,
        align: 'center',
        fixed: false,
        label: 'IVR Exit',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.PICK_UP,
        align: 'center',
        fixed: false,
        label: 'PickUp',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.TIMEOUT_EXIT,
        align: 'center',
        fixed: false,
        label: 'TimeOut Exit',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.JOIN_EMPTY,
        align: 'center',
        fixed: false,
        label: 'Join Empty',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.LEAVE_EMPTY,
        align: 'center',
        fixed: false,
        label: 'LeaveEmpty',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.JOIN_UNAVAIL,
        align: 'center',
        fixed: false,
        label: 'Join Unavailable',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.LEAVE_UNAVAIL,
        align: 'center',
        fixed: false,
        label: 'Leave Unavailable',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.FULL,
        align: 'center',
        fixed: false,
        label: 'Full',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.RECURRING_COUNT,
        align: 'center',
        fixed: false,
        label: 'Recurring Count',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.NEXT_DESTINATION,
        align: 'center',
        fixed: false,
        label: 'Next Destination',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.CALL_COUNT,
        align: 'center',
        fixed: false,
        label: 'Call Count',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.MAX_RING_TIME,
        align: 'center',
        fixed: false,
        label: 'Max Ring Time',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.NOT_IN_SLA_COUNT,
        align: 'center',
        fixed: false,
        label: 'Not in SLA Count',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.IN_SLA_COUNT,
        align: 'center',
        fixed: false,
        label: 'In SLA Count',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.AVG_RING_TIME,
        align: 'center',
        fixed: false,
        label: 'Avg Ring Time',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.ACTIVE_QUEUE_CALLS,
        align: 'center',
        fixed: false,
        label: 'Active Queue Calls',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.AGENTS_ASSIGNED_AND_AVAILABLE,
        align: 'center',
        fixed: false,
        label: 'Agents Assigned And Available',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.AGENTS_ASSIGNED_AND_UNAVAILABLE,
        align: 'center',
        fixed: false,
        label: 'Agents Assigned And Unavailable',
        minWidth: 130,
    },
    {
        prop: multiQueueColumnsPropNames.AVG_ANSWER_HOURLY,
        align: 'center',
        fixed: false,
        label: 'Avg Answer Hourly',
        minWidth: 130,
    },
]
